import React, { useEffect, useState } from 'react';
import { listTemplates, getTemplate, createTemplate, getDefaultTemplate, setDefaultTemplate, TemplateRow, TplItem } from './api';
import { getCard, cardType, rid, itemMaps, gpItems, lpItems, coItems } from './collect';
import type { Collect, Item } from './types';

interface Props {
  refId: string;
  card: { items: Item[] };
  edit: (fn: (c: Collect) => void) => void;
  toast: (m: string) => void;
  trackDefault?: boolean;
  label?: string;
  /** 카드 갈래 — gp · lp · co. 계정 기본 템플릿(마지막 사용)은 GP 카드만 따른다(2026-10-31 지도사 「LP 카드에 GP 기본세트가 디폴트로 나옴」) */
  kind?: 'gp' | 'lp' | 'co';
}

/** 기본 세트(내장) — 늘 맨 위에 · 고르면 그 항목으로 바꾼다(LP 는 주민등록등본 · 휴대폰번호만 받는 경우가 많다) */
const BUILTIN: Record<'gp' | 'lp' | 'co', { key: string; name: string; items: () => Item[] }> = {
  gp: { key: '__gp', name: 'GP 기본 — 교육 ID · 신분증 · 등본 · 수료증 등', items: gpItems },
  lp: { key: '__lp', name: 'LP 기본 — 주민등록등본 · 휴대폰번호만', items: lpItems },
  co: { key: '__co', name: '법인 기본 — 사업자등록증 · 정관 · 주주명부 · 재무제표 등', items: coItems },
};
const sameTitles = (a: Item[], b: Item[]) => a.length === b.length && a.every((x, i) => x.title === b[i].title);

const KNOWN = ['file', 'text', 'text_long', 'radio', 'checkbox'];
function toVType(t: string): string {
  if (KNOWN.includes(t)) return t;
  if (t === 'textarea' || t === 'long' || t === '장문') return 'text_long';
  if (t === '파일') return 'file';
  return 'text';
}
function toTType(t: string): string {
  return KNOWN.includes(t) ? t : 'text';
}

export default function TemplateBar({ refId, card, edit, toast, trackDefault: track0 = true, label = '베이스 템플릿 · 자료수집 공통', kind }: Props) {
  const k = kind || cardType(refId);
  const trackDefault = track0 && k === 'gp';
  const bi = BUILTIN[k];
  const [list, setList] = useState<TemplateRow[]>([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  // 지금 항목이 기본 세트 그대로면 그걸 고른 것으로 보여 준다(LP 카드가 GP 템플릿을 고른 것처럼 보이던 것)
  const [selected, setSelected] = useState(() => (sameTitles(card.items, bi.items()) ? bi.key : ''));

  useEffect(() => {
    listTemplates().then(setList).catch(() => setList([]));
    if (trackDefault) getDefaultTemplate().then((d) => { if (d && d.id) setSelected(d.id); }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setItems = (items: Item[]) => edit((c) => { getCard(c, refId).items = items; });
  const apply = async (id: string) => {
    if (!id) return;
    if (id === bi.key) { const items = bi.items(); setItems(items); setSelected(id); toast(`기본 세트 적용 · ${items.length}개 항목으로 교체됨`); return; }
    try {
      const t = await getTemplate(id);
      const items: Item[] = (t.items || []).map((it: TplItem) => ({
        id: rid(),
        title: it.label || '',
        type: toVType(it.type || 'text'),
        required: it.required !== false,
        sensitive: !!(it.sensitive || it.is_sensitive),
        map: '',
        maps: (it.extras && it.extras.maps) || [],
        options: Array.isArray(it.options) ? (it.options as string[]) : [],
        desc: it.description || '',
        links: (it.guides || []).filter((g) => (g.type || 'link') === 'link').map((g) => ({ name: g.label || g.url || '', url: g.url || '' })),
        files: (it.guides || []).filter((g) => g.type === 'file').map((g) => ({ name: g.fileName || '', filePath: g.filePath || g.s3Key || '', s3Key: g.s3Key || g.filePath || '' })),
        open: false,
      }));
      setItems(items);
      setSelected(id);
      if (trackDefault) setDefaultTemplate(id).catch(() => undefined);
      toast('템플릿 적용 · ' + items.length + '개 항목으로 교체됨' + (trackDefault ? ' (기본 템플릿으로 저장)' : ''));
    } catch (e) {
      toast((e as Error).message);
    }
  };

  const save = async () => {
    if (!name.trim()) { toast('템플릿 이름을 입력하세요'); return; }
    setBusy(true);
    try {
      const items = card.items.map((it) => ({
        label: it.title, desc: it.desc, required: it.required, sensitive: it.sensitive,
        type: toTType(it.type), options: it.options || [],
        extras: { maps: itemMaps(it) },
        guides: [
          ...it.links.map((l) => ({ type: 'link', label: l.name, url: l.url, fileName: '', filePath: '', s3Key: '' })),
          ...it.files.map((f) => ({ type: 'file', label: '', url: '', fileName: f.name, filePath: f.filePath || f.s3Key || '', s3Key: f.s3Key || f.filePath || '' })),
        ],
      }));
      const detail = await createTemplate({ name: name.trim(), items });
      if (detail && detail.id) { setSelected(detail.id); if (trackDefault) setDefaultTemplate(detail.id).catch(() => undefined); }
      toast('템플릿으로 저장됨' + (trackDefault ? ' · 기본 템플릿으로 지정' : '') + ' (단계 연동 포함)');
      setSaveOpen(false); setName('');
      listTemplates().then(setList).catch(() => undefined);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1 }}>{label}</span>
        {!saveOpen ? <button className="cact" onClick={() => setSaveOpen(true)}>💾 템플릿으로 저장</button> : null}
      </div>
      {saveOpen ? (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <input className="bizno" style={{ flex: 1, marginBottom: 0 }} value={name} onChange={(e) => setName(e.target.value)} placeholder="템플릿 이름 (예: 벤처투자 GP 기본 세트)" autoFocus />
          <button className="cact" onClick={save} disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
          <button className="cact" onClick={() => { setSaveOpen(false); setName(''); }}>취소</button>
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select className="tmpl" style={{ flex: 1 }} value={selected} onChange={(e) => apply(e.target.value)}>
          <option value="">템플릿 선택 안 함 · 선택하면 현재 항목이 교체됩니다</option>
          <option value={bi.key}>{bi.name}</option>
          {list.map((t) => <option value={t.id} key={t.id}>{t.name} ({t.itemCount}항목)</option>)}
        </select>
        {selected ? <button className="cact" onClick={() => apply(selected)}>적용</button> : null}
      </div>
    </>
  );
}
